<template lang="pug">
div(v-if='bottomline && !error')
  div(v-if='bottomline.total_review > 0' class='mt-10 lg:mt-0 lg:flex mb-10 items-center')
    div(v-if='averageScore' class='flex items-center align-middle justify-center lg:justify-start')
      div(class='text-[48px] font-normal') {{ averageScore.toFixed(1) }}
      div(class='flex flex-col ml-[10px]')
        div(class='flex pt-3 pr-[50px]')
          div(v-for='i in 5' :key='i' class='w-[18px] h-[18px] mr-2')
            img(v-if='i <= averageScore' src='/images/star-full-bw.svg' width='18' height='18' alt='full star')
            img(
              v-else-if='i > averageScore && i < averageScore + 1'
              src='/images/star-half-bw.svg'
              width='18'
              height='18'
              alt='half star'
            )
            img(v-else src='/images/star-empty-bw.svg' width='18' height='18' alt='empty star')

        span(class='text-sm font-normal mt-2') Based on {{ totalReviews }} reviews

    div(class='flex align-middle max-w-72 items-center mt-3 mb-8 m-auto justify-center lg:m-0 lg:justify-start lg:flex-grow')
      div(class='flex flex-col flex-grow')
        button(
          v-for='rating in [5, 4, 3, 2, 1]'
          :key='rating'
          :aria-label='`${rating} stars`'
          class='hover:underline cursor-pointer'
          :class='{ "opacity-50": selectedRating !== "all" && selectedRating !== rating.toString() }'
          @click='selectRating(rating)'
        )
          span(class='flex items-center')
            span(class='w-2 mr-2 text-right text-sm font-normal') {{ rating }}
            img(src='/images/star-full-bw.svg' class='w-[14px] h-[14px] mr-px' width='14' height='14' alt='full star')
            span(class='flex-grow ml-[22px] mr-11 h-1.5 bg-gray-lighter rounded-full max-w-36 mt-1')
              span(:style='{ width: getPercentage(rating) + "%" }' class='block bg-black h-full rounded-full')
            span(class='w-12 text-sm font-normal h-[25px]') {{ starCounts ? starCounts[rating] : 0 }}

    div(class='flex justify-center lg:flex-grow lg:justify-end min-w-36')
      button(
        aria-label='Write a review'
        class='rounded-full bg-black text-white px-4 py-2 text-sm font-bold max-w-36 w-full'
        @click='openWriteReviewModal'
      ) Write A Review
</template>

<script setup lang="ts">
const { $sitewideConfig, $overlay } = useNuxtApp()

const { yotpoId, selectedRating } = defineProps<{
  yotpoId: string
  selectedRating: string
}>()

const emit = defineEmits(['onGetStats', 'onRatingSelect'])

const error = ref(false)
const bottomline = ref<YotpoBottomline | null>(null)

onMounted(async () => {
  getBottomline()
})

const averageScore = computed(() => {
  if (!bottomline.value) return undefined
  return parseFloat(bottomline.value.average_score.toFixed(1))
})

const starCounts = computed(() => {
  if (!bottomline.value) return undefined
  return bottomline.value.star_distribution
})

const totalReviews = computed(() => {
  if (!bottomline.value) return undefined
  return bottomline.value.total_review
})

async function getBottomline() {
  const url = `https://api-cdn.yotpo.com/v1/widget/${$sitewideConfig.config.yotpoApiKey}/products/${yotpoId}/reviews.json`
  const res = ref<YotpoReviewsResponse | null>(null)

  try {
    res.value = await $fetch<YotpoReviewsResponse>(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })

    bottomline.value = res.value.response.bottomline
    emit('onGetStats', bottomline.value)
  } catch (err) {
    error.value = true
  }
}

function getPercentage(stars: number) {
  if (!starCounts.value || !totalReviews.value) return undefined
  return (starCounts.value[stars] / totalReviews.value) * 100
}

function openWriteReviewModal() {
  $overlay.open('yotpoWriteReviewModal')
}

function selectRating(rating: number) {
  if (selectedRating === rating.toString()) emit('onRatingSelect', 'all')
  else emit('onRatingSelect', rating.toString())
}
</script>
